/**
 * ResourceListView — Content type view for `resource_list`
 *
 * Displays a PageHeaderSimple with the item title/description and a
 * ResponsiveGrid of child `resource` items fetched from the catalog.
 * Very similar in spirit to DisseminationPage, but scoped to a single
 * resource_list container.
 *
 * Solves issue #116
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import { searchContent } from '@plone/volto/actions';
import { flattenToAppURL } from '@plone/volto/helpers';
import PageHeaderSimple from '../../Molecules/PageHeaderSimple';
import { ContentWide, PageSection } from '../../Layout';
import Pagination from '../../Molecules/Pagination/Pagination';
import { ResponsiveGrid } from '../../Atoms/ResponsiveGrid';
import TeaserCardVertical from '../../Molecules/TeaserCard/TeaserCardVertical';
import { resolveImageURL } from 'volto-essvoltocomponents/helpers';

const PAGE_SIZE = 12;
const SUBREQUEST = 'resourceListItems';

const GRID_BREAKPOINTS = [
  { minWidth: 0, columns: 1 },
  { minWidth: 768, columns: 2 },
  { minWidth: 1024, columns: 3 },
  { minWidth: 1280, columns: 4 },
];

const messages = defineMessages({
  loading: {
    id: 'resource_list_view.loading',
    defaultMessage: 'Loading…',
  },
  error: {
    id: 'resource_list_view.error',
    defaultMessage: 'There was an error loading the content. Please try again.',
  },
  empty: {
    id: 'resource_list_view.empty',
    defaultMessage: 'No resources found.',
  },
});

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const ResourceListView = ({ content }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  const sectionRef = useRef(null);

  const contentPath = content?.['@id'] ? flattenToAppURL(content['@id']) : '/';
  const subrequestKey = `${SUBREQUEST}-${contentPath}-${currentPage}`;

  const items = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.items || [],
  );
  const total = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.total || 0,
  );
  const loading = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loading ?? false,
  );
  const loaded = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.loaded ?? false,
  );
  const error = useSelector(
    (state) => state.search.subrequests?.[subrequestKey]?.error || null,
  );

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (contentPath && !loaded) {
      dispatch(
        searchContent(
          contentPath,
          {
            portal_type: ['resource'],
            review_state: 'published',
            sort_on: 'effective',
            sort_order: 'reverse',
            b_size: PAGE_SIZE,
            b_start: (currentPage - 1) * PAGE_SIZE,
            metadata_fields: ['_all'],
          },
          subrequestKey,
        ),
      );
    }
  }, [dispatch, contentPath, currentPage, subrequestKey, loaded]);

  const scrollToSection = () => {
    setTimeout(() => {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 0);
  };

  return (
    <div className="resource-list-view">
      <PageHeaderSimple
        title={content?.title || ''}
        description={
          content?.full_description?.data || content?.description || ''
        }
        showBackButton
      />

      <PageSection
        paddingTop="var(--ess-space-2xl)"
        paddingBottom="var(--ess-space-2xl)"
        ref={sectionRef}
      >
        <ContentWide>
          {error ? (
            <p className="resource-list-view__error" role="alert">
              {intl.formatMessage(messages.error)}
            </p>
          ) : loading || !loaded ? (
            <p className="resource-list-view__loading" aria-live="polite">
              {intl.formatMessage(messages.loading)}
            </p>
          ) : items.length === 0 ? (
            <p className="resource-list-view__empty">
              {intl.formatMessage(messages.empty)}
            </p>
          ) : (
            <>
              <div className="resource-list-view__listing">
                <ResponsiveGrid breakpoints={GRID_BREAKPOINTS}>
                  {items.map((item) => (
                    <TeaserCardVertical
                      key={item['@id']}
                      title={item.title || ''}
                      description={
                        item.short_description?.data ||
                        item.description?.data ||
                        ''
                      }
                      imageUrl={resolveImageURL(
                        item,
                        '/@@images/thumbnail_image/preview',
                      )}
                      url={item['@id'] || ''}
                      date={formatDate(item.effective)}
                    />
                  ))}
                </ResponsiveGrid>
              </div>

              {totalPages > 1 && (
                <div className="resource-list-view__pagination">
                  <Pagination
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                      scrollToSection();
                    }}
                  />
                </div>
              )}
            </>
          )}
        </ContentWide>
      </PageSection>
    </div>
  );
};

export default ResourceListView;
